import React, { useState, useRef, useEffect } from "react";
import uploadIcon from "./img/uploadIcon.png";
import folderIcon from "./img/folder.webp";
import cameraIcon from "./img/kamera.png";

import badExample from "./img/badEstateExample.jpg";
import goodExample from "./img/goodEstateExample.jpg";
import { Alert, Modal, ProgressBar } from "react-bootstrap";
import { useM365Context } from "./m365Context";
import { SpinnerGroup } from "./Utils/SpinnerGroup";

const INITIAL_DROP_ZONE = [
   {
      id: "left",
      label: "Hauptordner",
      rect: { top: 0, left: 0, width: "50%", height: "100%" },
      target: { driveId: "drive-links", parentId: "folder-links" },
   },
];

export default function SharepointFileUpload({ sharepointFileData, sharepointData }) {
   const { getSharepointData, getSharepointFile, uploadFileToSharepoint } = useM365Context();
   const [dropZones, setDropZones] = useState(INITIAL_DROP_ZONE);
   const [show, setShow] = useState(false);
   const [selectedFiles, setSelectedFiles] = useState([]);
   const [uploading, setUploading] = useState(false);
   const isUploading = uploading === "isUploading";
   const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
   const [error, setError] = useState(null);

   const [hoveredZoneId, setHoveredZoneId] = useState(null);
   const [dragActive, setDragActive] = useState(false);

   const fileInputRef = useRef(null);
   const cameraInputRef = useRef(null);
   const windowDragCounter = useRef(0);
   const zoneDragCounters = useRef({});

   const handleClose = () => {
      setShow(false);
      setError(null);
   };

   const handleShow = () => {
      setShow(true);
   };

   useEffect(() => {
      function handleBeforeUnload(event) {
         if (!isUploading) return;
         event.preventDefault();
         event.returnValue = ""; // erforderlich für Chrome
         return "";
      }

      if (isUploading) {
         window.addEventListener("beforeunload", handleBeforeUnload);
      }
      return () => {
         window.removeEventListener("beforeunload", handleBeforeUnload);
      };
   }, [isUploading]);

   useEffect(() => {
      if (sharepointData) {
         let dz = [
            {
               id: "0",
               label: "Hauptordner",
               rect: { top: 0, left: 0, width: "50%", height: "100%" },
               target: { driveId: sharepointFileData.driveId, parentId: sharepointFileData.parentId },
            },
         ];

         const subFolder = Object.values(sharepointData[sharepointFileData.driveId]).filter((item) => item.folder);

         subFolder.forEach((item, index) => {
            const heightSlot = 100 / subFolder.length;

            dz.push({
               id: "1_" + index,
               label: item.name,
               rect: { top: index * heightSlot + "%", left: "50%", width: "50%", height: heightSlot + "%" },
               target: { driveId: sharepointFileData.driveId, parentId: item.id },
            });
         });
         console.log(dz);

         setDropZones(dz);
      }
   }, [sharepointData]);

   function addFiles(fileList, target) {
      const newFiles = Array.from(fileList).map((file) => ({
         file,
         previewUrl: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
         target,
      }));
      setSelectedFiles((prev) => [...prev, ...newFiles]);
   }

   function handleFileInputChange(event) {
      if (event.target.files?.length) {
         addFiles(event.target.files, dropZones[0]?.target);
      }
      event.target.value = "";
   }

   function removeFile(index) {
      setSelectedFiles((prev) => {
         const removed = prev[index];
         if (removed.previewUrl) URL.revokeObjectURL(removed.previewUrl);
         return prev.filter((_, i) => i !== index);
      });
   }

   async function uploadFile(item) {
      const target = item.target ?? dropZones[0]?.target;
      console.log(item);

      return uploadFileToSharepoint(item.file, target.driveId, target.parentId, item.file.name);
   }

   async function handleUploadAll() {
      if (selectedFiles.length === 0) return;
      setUploading("isUploading");
      setError(null);
      setUploadProgress({ current: 0, total: selectedFiles.length });
      try {
         for (const item of selectedFiles) {
            await uploadFile(item);
            setUploadProgress((prev) => ({ ...prev, current: prev.current + 1 }));
         }
         selectedFiles.forEach((item) => {
            if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
         });
         setSelectedFiles([]);
      } catch (err) {
         setError("Beim Hochladen ist ein Fehler aufgetreten.");
      } finally {
         setUploading("uploadDone");
         setTimeout(() => {
            setUploading(false);
            setUploadProgress({ current: 0, total: 0 });
         }, 2000);
      }
   }

   // Page-weiter Scharfschalter: sobald irgendwo Dateien über die Seite
   // gezogen werden, wird dragActive=true -> alle Zonen werden sichtbar
   // und pointer-events auf ihnen wird freigeschaltet.
   useEffect(() => {
      function onWindowDragEnter(event) {
         if (!event.dataTransfer?.types?.includes("Files")) return;
         windowDragCounter.current += 1;
         setDragActive(true);
      }
      function onWindowDragLeave() {
         windowDragCounter.current -= 1;
         if (windowDragCounter.current <= 0) {
            windowDragCounter.current = 0;
            setDragActive(false);
         }
      }
      function onWindowDrop() {
         windowDragCounter.current = 0;
         setDragActive(false);
      }

      window.addEventListener("dragenter", onWindowDragEnter);
      window.addEventListener("dragleave", onWindowDragLeave);
      window.addEventListener("drop", onWindowDrop);

      return () => {
         window.removeEventListener("dragenter", onWindowDragEnter);
         window.removeEventListener("dragleave", onWindowDragLeave);
         window.removeEventListener("drop", onWindowDrop);
      };
   }, []);

   function makeZoneHandlers(zone) {
      const counterKey = zone.id;
      if (zoneDragCounters.current[counterKey] === undefined) {
         zoneDragCounters.current[counterKey] = 0;
      }

      return {
         onDragEnter(event) {
            event.preventDefault();
            if (!event.dataTransfer?.types?.includes("Files")) return;
            zoneDragCounters.current[counterKey] += 1;
            setHoveredZoneId(zone.id);
         },
         onDragOver(event) {
            event.preventDefault();
         },
         onDragLeave(event) {
            event.preventDefault();
            zoneDragCounters.current[counterKey] -= 1;
            if (zoneDragCounters.current[counterKey] <= 0) {
               zoneDragCounters.current[counterKey] = 0;
               setHoveredZoneId((current) => (current === zone.id ? null : current));
            }
         },
         onDrop(event) {
            event.preventDefault();
            zoneDragCounters.current[counterKey] = 0;
            setHoveredZoneId(null);

            if (event.dataTransfer?.files?.length) {
               addFiles(event.dataTransfer.files, zone.target);
               setShow(true);
            }
         },
      };
   }

   return (
      <div style={{ position: "relative" }}>
         {/* Sobald irgendwo im Fenster Dateien gezogen werden (dragActive),
             werden ALLE Zonen sichtbar (Rahmen + Label). Die Zone, über der
             die Maus gerade tatsächlich schwebt, wird zusätzlich stärker
             hervorgehoben (kräftigere Farbe/Rahmen). Ohne aktives Dragging
             sind die Zonen unsichtbar UND pointer-events: none, damit
             Klicks/Hover im Hintergrund normal funktionieren. */}
         {dropZones.map((zone) => {
            const isHovered = hoveredZoneId === zone.id;
            return (
               <div
                  key={zone.id}
                  {...makeZoneHandlers(zone)}
                  style={{
                     position: "fixed",
                     ...zone.rect,
                     zIndex: 100000,
                     pointerEvents: dragActive ? "auto" : "none",

                     padding: "8px",
                  }}
               >
                  <div
                     style={{
                        width: "100%",
                        height: "100%",
                        borderRadius: "12px",
                        transition: "border-color 0.15s ease, background-color 0.15s ease",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "16px",
                        fontWeight: isHovered ? "bold" : "normal",
                        color: isHovered ? "#050" : "#333",
                        ...(dragActive
                           ? isHovered
                              ? {
                                   background: "#0905",
                                   backdropFilter: "blur(2px)",
                                   border: "4px dashed #090",
                                }
                              : {
                                   background: "#0902",
                                   backdropFilter: "blur(1px)",
                                   border: "3px dashed #0006",
                                }
                           : {
                                background: "transparent",
                                border: "0px dashed transparent",
                             }),
                     }}
                  >
                     {dragActive && zone.label}
                  </div>
               </div>
            );
         })}

         <button
            onClick={handleShow}
            className="custom-button"
            style={{
               position: "absolute",
               width: "56px",
               right: "240px",
               zIndex: 0,
            }}
            title="Dateien hochladen"
         >
            <img src={uploadIcon} />
         </button>

         <Modal
            show={show}
            size="xl"
            onHide={handleClose}
            dialogClassName="xxl-modal"
            backdrop={isUploading ? "static" : true}
            keyboard={!isUploading}
         >
            <Modal.Header closeButton={!isUploading}>
               <Modal.Title>Dateien hochladen</Modal.Title>
            </Modal.Header>
            <Modal.Body>
               <div style={{ position: "relative", height: "70vh" }}>
                  {dropZones.map((zone) => {
                     return (
                        <div
                           style={{
                              position: "absolute",
                              ...zone.rect,
                              padding: 4,
                           }}
                        >
                           <div
                              className="p-2"
                              style={{
                                 width: "100%",
                                 height: "100%",
                                 display: "flex",
                                 alignItems: "center",
                                 flexDirection: "column",
                                 justifyContent: "center",
                                 gap: 10,
                                 border: "solid 1px #ddd",
                                 borderRadius: 20,
                              }}
                           >
                              <strong>{zone.label}</strong>
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10 }}>
                                 <div style={{ display: "flex" }}>
                                    <input
                                       ref={fileInputRef}
                                       type="file"
                                       multiple
                                       onChange={handleFileInputChange}
                                       style={{ display: "none" }}
                                    />
                                    <button className="custom-button" onClick={() => fileInputRef.current?.click()}>
                                       <img src={folderIcon} alt="Dateien auswählen" title="Dateien auswählen" />
                                    </button>
                                 </div>
                                 <div>
                                    <input
                                       ref={cameraInputRef}
                                       type="file"
                                       accept="image/*"
                                       capture="environment"
                                       onChange={handleFileInputChange}
                                       style={{ display: "none" }}
                                    />
                                    <button className="custom-button" onClick={() => cameraInputRef.current?.click()}>
                                       <img src={cameraIcon} alt="Foto aufnehmen" title="Foto aufnehmen" />
                                    </button>
                                 </div>
                              </div>
                              {selectedFiles.length > 0 && (
                                 <ul
                                    style={{
                                       listStyle: "none",
                                       padding: 0,
                                       maxHeight: "calc(70vh - 140px)",
                                       overflowY: "auto",
                                       scrollbarWidth: "thin",
                                       scrollbarColor: "#ddd transparent",
                                    }}
                                 >
                                    {selectedFiles
                                       .filter((item) => {
                                          return item.target?.parentId === zone.target.parentId;
                                       })
                                       .map((item, index) => (
                                          <li
                                             key={`${item.file.name}-${index}`}
                                             style={{
                                                display: "flex",
                                                alignItems: "center",
                                                position: "relative",
                                                gap: "10px",
                                                padding: "6px 0",
                                                borderBottom: "1px solid #eee",
                                             }}
                                          >
                                             {item.previewUrl ? (
                                                <img
                                                   src={item.previewUrl}
                                                   alt={item.file.name}
                                                   style={{
                                                      width: "100%",
                                                      borderRadius: "6px",
                                                      flexShrink: 0,
                                                   }}
                                                />
                                             ) : (
                                                <>
                                                   <div
                                                      style={{
                                                         width: "60px",
                                                         height: "60px",
                                                         borderRadius: "6px",
                                                         background: "#eee",
                                                         display: "flex",
                                                         alignItems: "center",
                                                         justifyContent: "center",
                                                         fontSize: "10px",
                                                         flexShrink: 0,
                                                      }}
                                                   >
                                                      {item.file.name.split(".").pop()}
                                                   </div>
                                                   <span
                                                      style={{
                                                         marginRight: "70px",
                                                         width: "350px",
                                                         overflow: "hidden",
                                                      }}
                                                   >
                                                      {item.file.name}
                                                   </span>
                                                </>
                                             )}

                                             <button
                                                style={{
                                                   position: "absolute",
                                                   top: 10,
                                                   right: 5,
                                                   padding: "15px 20px",
                                                   background: "#000a",
                                                }}
                                                onClick={() => removeFile(index)}
                                                title="Entfernen"
                                             >
                                                ✕
                                             </button>
                                          </li>
                                       ))}
                                 </ul>
                              )}
                           </div>
                        </div>
                     );
                  })}
               </div>

               {isUploading && (
                  <div style={{ marginTop: 12 }}>
                     <ProgressBar
                        now={(uploadProgress.current / Math.max(uploadProgress.total, 1)) * 100}
                        label={`${uploadProgress.current}/${uploadProgress.total}`}
                     />
                     <div style={{ fontSize: 13, color: "#666", marginTop: 4 }}>
                        Bitte diese Seite nicht verlassen, während der Upload läuft.
                     </div>
                  </div>
               )}

               {error && <div style={{ color: "red", marginTop: "8px" }}>{error}</div>}
               {uploading === "uploadDone" && <Alert variant="success">Hochladen erfolgreich!</Alert>}
            </Modal.Body>
            <Modal.Footer>
               <button className="custom-button" onClick={handleClose}>
                  Abbrechen
               </button>
               <button
                  className="custom-button"
                  onClick={handleUploadAll}
                  disabled={uploading === "isUploading" || selectedFiles.length === 0}
               >
                  {uploading === "isUploading" ? <SpinnerGroup /> : "Hochladen"}
               </button>
            </Modal.Footer>
         </Modal>
      </div>
   );
}
